import { useId, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CircleAlert, CircleCheck, Eye, EyeOff, Lock } from 'lucide-react'
import { stripClass } from '@/components/admin/styles'
import { cn } from '@/lib/utils'

export const authButtonClass =
  'rounded-lg bg-ink px-4 py-2.5 text-sm font-semibold text-paper transition-[background-color,translate] duration-150 hover:bg-ink/85 active:translate-y-px disabled:opacity-50'

export const authLinkClass =
  'rounded text-sm font-medium text-ink-soft underline-offset-4 transition-colors hover:text-ink hover:underline'

type AuthCardProps = {
  headingId: string
  title: string
  intro?: ReactNode
  /** Illustrazione sopra l'occhiello (es. esito di un invio). */
  icon?: ReactNode
  onBack?: () => void
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
  children: ReactNode
}

/**
 * Card dei form di accesso, fatta come il selettore lingua: carta d'imbarco con la striscia "Area riservata"
 * e la perforazione, poi titolo, sottotitolo e ritorno al login facoltativo.
 */
export function AuthCard({ headingId, title, intro, icon, onBack, onSubmit, children }: AuthCardProps) {
  const { t } = useTranslation()
  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-sm overflow-hidden rounded-xl border-2 border-ink bg-paper shadow-[4px_4px_0_var(--color-ink)] motion-safe:animate-reveal"
      aria-labelledby={headingId}
    >
      <p className={cn(stripClass, 'flex items-center justify-between px-6 pt-3.5 pb-3 sm:px-8')}>
        {t('auth.eyebrow')}
        <Lock aria-hidden="true" className="size-3.5" />
      </p>
      <span aria-hidden="true" className="ticket-perforation block" />
      <div className="p-6 sm:p-8">
        {onBack && (
          <button type="button" onClick={onBack} className={`-ml-0.5 mb-6 inline-flex items-center gap-1.5 ${authLinkClass}`}>
            <ArrowLeft aria-hidden className="size-4" />
            {t('auth.forgot.back')}
          </button>
        )}
        {icon && <div className="mb-5">{icon}</div>}
        <h1 id={headingId} className="font-display text-3xl font-medium tracking-tight text-ink">
          {title}
        </h1>
        {intro && <p className="mt-2 text-sm leading-relaxed text-ink-soft">{intro}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </form>
  )
}

const NOTICE_TONES = {
  success: { box: 'bg-sea/10 ring-sea/50 text-sea-ink', icon: 'text-sea-ink', Icon: CircleCheck, role: 'status' },
  error: { box: 'bg-coral/10 ring-coral/60 text-coral-ink', icon: 'text-coral-ink', Icon: CircleAlert, role: 'alert' },
} as const

type AuthNoticeProps = { tone: keyof typeof NOTICE_TONES; title?: string; children: ReactNode }

/** Esito di un'azione con icona; `title` facoltativo sopra il testo. */
export function AuthNotice({ tone, title, children }: AuthNoticeProps) {
  const { box, icon, Icon, role } = NOTICE_TONES[tone]
  return (
    <div role={role} className={`flex gap-3 rounded-lg p-3.5 text-sm ring-1 ring-inset motion-safe:animate-reveal ${box}`}>
      <Icon aria-hidden className={`mt-px size-5 shrink-0 ${icon}`} />
      <div className="leading-snug">
        {title && <p className="font-semibold">{title}</p>}
        <p className={title ? 'mt-0.5 opacity-90' : ''}>{children}</p>
      </div>
    </div>
  )
}

type AuthFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  name: string
  /** Azione allineata a destra dell'etichetta (es. "Password dimenticata?"). */
  labelAction?: ReactNode
  /** Testo d'aiuto sotto il campo. */
  hint?: string
}

const inputClass =
  'w-full rounded-lg border border-ink-soft/70 bg-paper px-3 py-2 text-ink transition-colors focus:border-ink focus:outline-none focus:ring-2 focus:ring-sea/30'

/** Campo con etichetta dei form del sito; le password hanno il pulsante mostra/nascondi. */
export function AuthField({ label, labelAction, hint, type, ...input }: AuthFieldProps) {
  const { t } = useTranslation()
  const id = useId()
  const [isVisible, setIsVisible] = useState(false)
  const isPassword = type === 'password'
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {labelAction}
      </div>
      <div className="relative">
        <input
          {...input}
          id={id}
          type={isPassword && isVisible ? 'text' : type}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className={isPassword ? `${inputClass} pr-10` : inputClass}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setIsVisible((visible) => !visible)}
            aria-label={t(isVisible ? 'auth.fields.hidePassword' : 'auth.fields.showPassword')}
            aria-pressed={isVisible}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-ink-soft transition-colors hover:text-ink focus-visible:text-ink focus-visible:-outline-offset-2"
          >
            {isVisible ? <EyeOff aria-hidden className="size-4" /> : <Eye aria-hidden className="size-4" />}
          </button>
        )}
      </div>
      {hint && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-ink-soft">
          {hint}
        </p>
      )}
    </div>
  )
}
